<template>
  <div class="auth-page">
    <!-- Background elements -->
    <div class="bg-glow"></div>
    <div class="bg-grid"></div>
    
    <!-- Navigation -->
    <nav class="navbar">
      <router-link to="/" class="nav-logo">
        <AppLogo class="logo-img" />
      </router-link>
      <router-link to="/login" class="btn-login">Login</router-link>
    </nav>

    <!-- Registration Form -->
    <div class="auth-container">
      <div class="auth-card">
        <div class="auth-header">
          <h1>Create your account</h1>
          <p>Start building tailored resumes in seconds</p>
        </div>

        <!-- Referral Banner -->
        <div v-if="referralValid" class="referral-banner">
          <i class="fa fa-gift"></i>
          <span>Referred by <strong>{{ referrerName }}</strong> — Make a purchase to unlock rewards for both of you!</span>
        </div>

        <form @submit.prevent="signUp">
          <div class="form-row">
            <div class="form-group">
              <label for="firstName">First Name</label>
              <input
                type="text"
                id="firstName"
                v-model="firstName"
                placeholder="John"
              />
            </div>
            <div class="form-group">
              <label for="lastName">Last Name</label>
              <input
                type="text"
                id="lastName"
                v-model="lastName"
                placeholder="Doe"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="email">Email</label>
            <input
              type="email"
              id="email"
              v-model="email"
              placeholder="you@example.com"
              required
            />
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <input
              type="password"
              id="password"
              v-model="password"
              placeholder="••••••••"
              minlength="8"
              required
            />
            <p class="input-hint">At least 8 characters with a letter and number</p>
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              v-model="confirmPassword"
              placeholder="••••••••"
              required
            />
          </div>

          <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
          <p v-if="successMessage" class="success-message">{{ successMessage }}</p>

          <button type="submit" class="btn-primary">Create Account</button>
        </form>

        <div class="divider">
          <span>or continue with</span>
        </div>

        <div class="social-buttons">
          <button class="social-btn" @click="signUpWithProvider('google')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Google
          </button>
          <button class="social-btn" @click="signUpWithProvider('github')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            GitHub
          </button>
          <button class="social-btn linkedin" @click="signUpWithProvider('linkedin')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            LinkedIn
          </button>
        </div>

        <p class="auth-footer">
          Already have an account? <router-link to="/login">Sign in</router-link>
        </p>

        <p class="terms">
          By creating an account, you agree to our
          <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter, useRoute } from 'vue-router';
import axios, { formatApiErrorMessage } from '@/axios';
import AppLogo from '@/components/AppLogo.vue';

export default {
  components: { AppLogo },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      errorMessage: '',
      successMessage: '',
      referralCode: '',
      referralValid: false,
      referrerName: '',
    };
  },
  methods: {
    validatePassword(password) {
      const errors = [];
      if (!password || password.length < 8) {
        errors.push("Password must be at least 8 characters");
      }
      if (!/[A-Za-z]/.test(password)) {
        errors.push("Password must contain at least one letter");
      }
      if (!/[0-9]/.test(password)) {
        errors.push("Password must contain at least one number");
      }
      return errors;
    },
    async validateReferralCode(code) {
      if (!code) return;
      
      try {
        const response = await axios.get(`/api/referral/validate/${code}`);
        if (response.data.valid) {
          this.referralValid = true;
          this.referrerName = response.data.referrerName;
          this.referralCode = code.toUpperCase();
        }
      } catch (error) {
        // Invalid code, just ignore
        console.log('Invalid referral code');
      }
    },
    async signUp() {
      this.errorMessage = '';
      this.successMessage = '';
      
      if (this.password !== this.confirmPassword) {
        this.errorMessage = 'Passwords do not match';
        return;
      }
      
      const passwordErrors = this.validatePassword(this.password);
      if (passwordErrors.length > 0) {
        this.errorMessage = passwordErrors.join(". ");
        return;
      }

      try {
        await axios.post('/api/auth/register', {
          email: this.email,
          password: this.password,
          firstName: this.firstName,
          lastName: this.lastName,
          referralCode: this.referralCode || undefined,
        });

        this.successMessage = 'Account created! Please check your email to verify.';
        setTimeout(() => this.goToLogin(), 2000);

      } catch (error) {
        this.errorMessage = formatApiErrorMessage(error, 'An error occurred. Please try again.');
      }
    },
    signUpWithProvider(provider) {
      const backendUrl = process.env.VUE_APP_API_URL || "https://api.resymaid.com";
      window.location.href = `${backendUrl}/api/auth/${provider}`;
    },
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const goToLogin = () => {
      router.push('/login');
    };

    return {
      goToLogin,
      route,
    };
  },
  mounted() {
    // Check for referral code in URL
    const refCode = this.$route.query.ref;
    if (refCode) {
      this.validateReferralCode(refCode);
    }
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.auth-page {
  min-height: 100vh;
  background: #0a0a0f;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.bg-glow {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 400px;
  background: radial-gradient(ellipse, rgba(139, 92, 246, 0.15) 0%, transparent 70%);
  filter: blur(60px);
  pointer-events: none;
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(rgba(139, 92, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(139, 92, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
}

/* Navbar */
.navbar {
  position: relative;
  z-index: 10;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
}

.nav-logo {
  text-decoration: none;
  flex-shrink: 1;
  min-width: 0;
}

.logo-img {
  height: 56px;
  max-width: 200px;
  width: auto;
  object-fit: contain;
}

.btn-login {
  background: #ffffff;
  color: #0a0a0f;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;
}

.btn-login:hover {
  background: #e4e4e7;
}

/* Auth Container */
.auth-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  z-index: 1;
}

.auth-card {
  width: 100%;
  max-width: 480px;
  background: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 2.5rem;
  backdrop-filter: blur(10px);
}

.auth-header {
  text-align: center;
  margin-bottom: 2rem;
}

.auth-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
}

.auth-header p {
  color: #71717a;
  font-size: 15px;
}

/* Referral Banner */
.referral-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(239, 68, 68, 0.1));
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 1.5rem;
}

.referral-banner i {
  color: #fbbf24;
  font-size: 18px;
}

.referral-banner span {
  color: #fbbf24;
  font-size: 14px;
}

.referral-banner strong {
  color: #ffffff;
}

/* Form */
.form-row {
  display: flex;
  gap: 1rem;
}

.form-row .form-group {
  flex: 1;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  color: #a1a1aa;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.form-group input {
  width: 100%;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #ffffff;
  font-size: 15px;
  transition: all 0.2s;
}

.form-group input::placeholder {
  color: #52525b;
}

.form-group input:focus {
  outline: none;
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.input-hint {
  color: #52525b;
  font-size: 12px;
  margin-top: 6px;
}

.error-message {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 1rem;
  text-align: center;
}

.success-message {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #4ade80;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 1rem;
  text-align: center;
}

.btn-primary {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 20px rgba(139, 92, 246, 0.4);
}

/* Divider */
.divider {
  display: flex;
  align-items: center;
  margin: 1.75rem 0;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
}

.divider span {
  color: #52525b;
  font-size: 13px;
  padding: 0 1rem;
}

/* Social Buttons */
.social-buttons {
  display: flex;
  gap: 1rem;
}

.social-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #a1a1aa;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.social-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.social-btn svg {
  flex-shrink: 0;
}

.social-btn.linkedin {
  background: rgba(0, 119, 181, 0.1);
  border-color: rgba(0, 119, 181, 0.3);
}

.social-btn.linkedin:hover {
  background: rgba(0, 119, 181, 0.2);
}

.social-btn.linkedin svg {
  color: #0077b5;
}

/* Footer */
.auth-footer {
  text-align: center;
  margin-top: 2rem;
  color: #71717a;
  font-size: 14px;
}

.auth-footer a {
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 500;
}

.auth-footer a:hover {
  color: #a78bfa;
}

.terms {
  text-align: center;
  margin-top: 1.5rem;
  color: #52525b;
  font-size: 12px;
}

.terms a {
  color: #71717a;
  text-decoration: none;
}

.terms a:hover {
  color: #a1a1aa;
}

/* Responsive */
@media (max-width: 768px) {
  .navbar {
    padding: 0.75rem 1rem;
  }

  .logo-img {
    height: 32px;
    max-width: 140px;
  }

  .btn-login {
    font-size: 13px;
    padding: 7px 14px;
  }
}

@media (max-width: 480px) {
  .auth-card {
    padding: 2rem 1.5rem;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .social-buttons {
    flex-direction: column;
  }
}
</style>
